import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import LessonAttachments from '../../student-lesson-page/components/LessonAttachments';

const ChapterList = ({ 
  chapters = [], 
  currentTime = 0, 
  onChapterClick,
  isCollapsed = false,
  onToggleCollapse,
  attachments = []
}) => {
  const [expandedChapter, setExpandedChapter] = useState(null);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds?.toString()?.padStart(2, '0')}`;
  };

  const getCurrentChapter = () => {
    return chapters?.find((chapter, index) => {
      const nextChapter = chapters?.[index + 1];
      return currentTime >= chapter?.time && (!nextChapter || currentTime < nextChapter?.time);
    });
  };

  const currentChapter = getCurrentChapter();

  const handleChapterClick = (chapter) => {
    onChapterClick?.(chapter?.time);
  };

  const toggleChapterExpansion = (chapterId) => {
    setExpandedChapter(expandedChapter === chapterId ? null : chapterId);
  };

  if (chapters?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="font-heading font-medium text-foreground">
            Material de Apoio
          </h3>
        </div>
        <div className="p-4">
          <LessonAttachments attachments={attachments} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="List" size={20} className="text-foreground" />
          <h3 className="font-heading font-medium text-foreground">
            Capítulos ({chapters?.length})
          </h3>
        </div>
        <Button
          variant="ghost"
          onClick={onToggleCollapse}
          iconName={isCollapsed ? "ChevronDown" : "ChevronUp"}
          iconSize={16}
          className="text-muted-foreground hover:text-foreground"
        />
      </div>
      {/* Chapter List */}
      {!isCollapsed && (
        <div className="max-h-96 overflow-y-auto">
          {chapters?.map((chapter, index) => {
            const isActive = currentChapter?.id === chapter?.id;
            const isExpanded = expandedChapter === chapter?.id;
            
            return (
              <div key={chapter?.id} className="border-b border-border last:border-b-0">
                <button
                  onClick={() => handleChapterClick(chapter)}
                  className={`w-full flex items-center space-x-4 p-4 text-left transition-micro hover:bg-accent hover:text-accent-foreground ${
                    isActive ? 'bg-primary/10 border-r-2 border-r-primary' : ''
                  }`}
                >
                  {/* Chapter Number */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {index + 1}
                  </div>

                  {/* Chapter Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-medium truncate ${
                      isActive ? 'text-primary' : 'text-foreground'
                    }`}>
                      {chapter?.title}
                    </h4>
                    <div className="flex items-center space-x-3 mt-1">
                      <span className="text-sm text-muted-foreground font-caption">
                        {formatTime(chapter?.time)}
                      </span>
                      {chapter?.duration && (
                        <>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-sm text-muted-foreground font-caption">
                            {formatTime(chapter?.duration)} min
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Status Indicator */}
                  <div className="flex items-center space-x-2">
                    {isActive && (
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                        <span className="text-xs text-primary font-medium font-caption">
                          Reproduzindo
                        </span>
                      </div>
                    )}
                    
                    {chapter?.description && (
                      <Button
                        variant="ghost"
                        onClick={(e) => {
                          e?.stopPropagation();
                          toggleChapterExpansion(chapter?.id);
                        }}
                        iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
                        iconSize={16}
                        className="text-muted-foreground hover:text-foreground p-1"
                      />
                    )}
                  </div>
                </button>
                {/* Chapter Description */}
                {isExpanded && chapter?.description && (
                  <div className="px-4 pb-4 pl-16">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {chapter?.description}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Current Chapter Summary */}
      {currentChapter && (
        <div className="p-4 bg-muted/50 border-t border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Play" size={16} className="text-primary" />
            <div>
              <p className="text-sm font-medium text-foreground">
                Reproduzindo agora
              </p>
              <p className="text-xs text-muted-foreground font-caption">
                {currentChapter?.title} • {formatTime(currentTime)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChapterList;